// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Chimes and bells — modal synthesis (M3-23, 07 §3.2).
//
// A struck metal bar is not a note with harmonics. Its partials are **inharmonic**: a free bar
// rings at f, 2.76f, 5.40f, 8.93f, and those ratios are why a chime sounds like metal rather
// than like an organ. Model it with a harmonic series and you get something that is recognisably
// a musical instrument and recognisably not a pinball machine.
//
// Modelling it correctly is also the cheap option — a decaying sinusoid per partial, four of
// them — which is the happy case where the physics and the budget agree.
//
// Two things a chime does that a coil does not:
//
//   * **The higher partials die first.** A real bar loses its top almost immediately and then
//     hums on the fundamental. Equal decays give a bright, synthetic ring that never softens,
//     and it is the single clearest tell of a modelled bell that has not been listened to.
//   * **It has a pitch, and the pitch is the point.** Every other one-shot in the product is
//     baked in eight variants at ±4% so a repeat never sounds looped. Doing that to a chime
//     would put the machine out of tune with itself, which is a far worse artefact than the one
//     the variants exist to avoid — so a chime is baked **once**.
//
//     That was measured rather than assumed. The first attempt varied the strike instead of the
//     pitch, and two variants of an 880 Hz bar differed in **88 samples out of 29,000**: the
//     hammer is one millisecond and the ring is half a second, so the whole tail was a byte-for-
//     byte duplicate. Which is also the physical answer — a bar struck twice by the same hammer
//     *does* sound the same, and a machine whose chimes varied would sound broken rather than
//     alive.

import {
  addNoiseBurst,
  addPartial,
  Biquad,
  fadeOut,
  normalise,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'

/**
 * The three bars, Hz (07 §3.2).
 *
 * 660, 880 and 1320 are E, A and E an octave up — a fourth and then a fifth, which is the
 * interval set every electromechanical machine used, because it is consonant in any order.
 * Lanes, bonus ticks and the pop-bumper ring all draw from these three.
 */
export const CHIME_BARS = [660, 880, 1320] as const
export type ChimeBar = 'chimeLow' | 'chimeMid' | 'chimeHigh'
export const CHIME_NAMES: readonly ChimeBar[] = ['chimeLow', 'chimeMid', 'chimeHigh']

/** The ideal free-bar ratios. Inharmonic, which is what makes it metal. */
export const BAR_PARTIALS = [1, 2.76, 5.4, 8.93] as const

/** Relative amplitude per partial: the fundamental carries the note, the rest colour the strike. */
const PARTIAL_GAIN = [1, 0.42, 0.19, 0.09] as const

/** How long the fundamental rings. A pinball chime is damped — it is a bar, not a tubular bell. */
export const CHIME_DECAY_MS = 520

/**
 * How much faster each partial decays than the fundamental.
 *
 * Higher modes radiate and damp faster in a real bar, so the tone darkens as it fades. The
 * exponent is a fit rather than a derivation: it puts the top partial at about a fifth of the
 * fundamental's ring, which is where a struck bar sounds like it stops being bright.
 */
const DECAY_FALLOFF = 0.72

/** The strike: 1 ms of filtered noise (07 §3.2), the hammer rather than the bar. */
const STRIKE_MS = 1
const STRIKE_HZ = 3200
const STRIKE_GAIN = 0.35

export function chimeLengthMs(): number {
  return CHIME_DECAY_MS * 1.15 + 8
}

/** Render one chime bar. One per bar: see the note above about what a second one would buy. */
export function renderChime(
  fundamentalHz: number,
  sampleRate: number,
  noise: NoiseSource,
): Samples {
  const out = new Float32Array(samplesFor(chimeLengthMs(), sampleRate))

  for (let i = 0; i < BAR_PARTIALS.length; i++) {
    const ratio = BAR_PARTIALS[i]!
    addPartial(
      out,
      sampleRate,
      fundamentalHz * ratio,
      PARTIAL_GAIN[i]!,
      CHIME_DECAY_MS / Math.pow(ratio, DECAY_FALLOFF),
    )
  }

  addNoiseBurst(out, sampleRate, noise, Biquad.bandpass(STRIKE_HZ, 1.2, sampleRate), STRIKE_GAIN, STRIKE_MS)

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

/** The bar a chime name refers to. */
export function barFor(name: ChimeBar): number {
  return CHIME_BARS[CHIME_NAMES.indexOf(name)] ?? CHIME_BARS[0]
}
